/**
 * AI Chat Interface Animation
 * 2026 创意动画趋势 - AI 聊天界面动画
 *
 * 技术要点：
 * 1. 打字机效果（逐字显示）
 * 2. 消息气泡滑入动画
 * 3. AI 思考过程可视化（脉动光点）
 * 4. 打字光标闪烁
 * 5. 消息队列动画
 * 6. Spring 物理进入动画
 * 7. 渐变背景
 * 8. 时间戳淡入效果
 *
 * 适用场景：
 * - AI 产品演示
 * - ChatGPT 风格视频
 * - AI 助手介绍
 * - 科技产品展示
 * - 教育视频
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// 消息类型定义
interface Message {
  role: 'user' | 'ai';
  content: string;
  timestamp: string;
}

// 聊天消息数据
const chatMessages: Message[] = [
  {
    role: 'user',
    content: '你好，能帮我写一首关于春天的诗吗？',
    timestamp: '14:23',
  },
  {
    role: 'ai',
    content:
      '当然可以！这是一首关于春天的诗：\n\n春暖花开鸟语香，\n绿草如茵满庭芳。\n微风拂面心神爽，\n万物复苏换新装。',
    timestamp: '14:23',
  },
  {
    role: 'user',
    content: '太棒了！能再写一首关于秋天的吗？',
    timestamp: '14:24',
  },
  {
    role: 'ai',
    content:
      '好的，这是关于秋天的诗：\n\n秋高气爽叶金黄，\n硕果累累满枝香。\n天高云淡雁南飞，\n收获时节喜洋洋。',
    timestamp: '14:24',
  },
];

// 主组件
export const AIChatInterface: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 渐变背景
  const backgroundGradient = `linear-gradient(135deg,
    hsl(${(frame * 0.5) % 360}, 70%, 95%) 0%,
    hsl(${(frame * 0.5 + 60) % 360}, 70%, 90%) 100%)`;

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 15], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleY = spring({
    frame,
    fps,
    config: { damping: 200 },
    from: -50,
    to: 0,
  });

  return (
    <AbsoluteFill
      style={{
        background: backgroundGradient,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: 60,
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
    >
      {/* 标题 */}
      <div
        style={{
          fontSize: 80,
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          marginBottom: 40,
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
          textShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
        }}
      >
        AI 聊天助手
      </div>

      {/* 聊天容器 */}
      <div
        style={{
          width: '100%',
          maxWidth: 1400,
          height: 800,
          backgroundColor: 'white',
          borderRadius: 30,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 聊天头部 */}
        <div
          style={{
            padding: '30px 40px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontSize: 40,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 20,
          }}
        >
          {/* AI 头像 */}
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 30,
            }}
          >
            🤖
          </div>
          <div>
            <div>智能对话助手</div>
            <div style={{ fontSize: 20, opacity: 0.8 }}>在线</div>
          </div>
        </div>

        {/* 消息区域 */}
        <div
          style={{
            flex: 1,
            padding: 40,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 30,
          }}
        >
          {chatMessages.map((msg, index) => {
            const messageStartFrame = 30 + index * 90;
            const isVisible = frame >= messageStartFrame;

            if (!isVisible) return null;

            const messageFrame = frame - messageStartFrame;

            // 消息滑入动画
            const slideX = spring({
              frame: messageFrame,
              fps,
              config: { damping: 200 },
              from: msg.role === 'user' ? 100 : -100,
              to: 0,
            });

            // 打字机效果（仅 AI 消息）
            const typewriterProgress = msg.role === 'ai'
              ? interpolate(messageFrame, [0, 60], [0, 1], {
                  extrapolateRight: 'clamp',
                })
              : 1;

            const displayedText = msg.role === 'ai'
              ? msg.content.substring(0, Math.floor(msg.content.length * typewriterProgress))
              : msg.content;

            // 时间戳淡入
            const timestampOpacity = interpolate(messageFrame, [50, 60], [0, 0.6], {
              extrapolateRight: 'clamp',
            });

            // 思考动画（AI 消息前 10 帧）
            const showThinking = msg.role === 'ai' && messageFrame < 10;
            const thinkingOpacity = interpolate(messageFrame, [0, 10], [1, 0], {
              extrapolateRight: 'clamp',
            });

            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  transform: `translateX(${slideX}px)`,
                  opacity: 1,
                }}
              >
                {/* 思考动画 */}
                {showThinking && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      marginBottom: 10,
                      opacity: thinkingOpacity,
                    }}
                  >
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: '#667eea',
                          animation: `pulse 1s ease-in-out ${i * 0.2}s infinite`,
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* 消息气泡 */}
                <div
                  style={{
                    maxWidth: '70%',
                    padding: '25px 35px',
                    borderRadius: msg.role === 'user'
                      ? '25px 25px 5px 25px'
                      : '25px 25px 25px 5px',
                    backgroundColor: msg.role === 'user'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : '#f5f5f5',
                    color: msg.role === 'user' ? 'white' : '#333',
                    fontSize: 28,
                    lineHeight: 1.6,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : '#f5f5f5',
                    whiteSpace: 'pre-line',
                    position: 'relative',
                  }}
                >
                  {displayedText}

                  {/* 打字光标 */}
                  {msg.role === 'ai' && typewriterProgress < 1 && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 2,
                        height: 28,
                        backgroundColor: '#667eea',
                        marginLeft: 2,
                        animation: 'blink 1s infinite',
                      }}
                    />
                  )}
                </div>

                {/* 时间戳 */}
                <div
                  style={{
                    fontSize: 18,
                    color: '#999',
                    marginTop: 8,
                    opacity: timestampOpacity,
                  }}
                >
                  {msg.timestamp}
                </div>
              </div>
            );
          })}
        </div>

        {/* 输入框 */}
        <div
          style={{
            padding: '30px 40px',
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: 20,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              flex: 1,
              padding: '20px 30px',
              borderRadius: 25,
              backgroundColor: '#f5f5f5',
              fontSize: 26,
              color: '#999',
            }}
          >
            输入消息...
          </div>
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 30,
              color: 'white',
            }}
          >
            ➤
          </div>
        </div>
      </div>

      {/* CSS 动画 */}
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}
      </style>
    </AbsoluteFill>
  );
};
